import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, ArrowRight, ArrowLeft, X, Upload, Image, Globe, Users } from '@phosphor-icons/react';
import { useAccount, useContractWrite, usePublicClient, useNetwork, useBalance, useWalletClient, useContractRead } from 'wagmi';
import { ADDRESSES, CONFIG, TOKEN_NAMES, TOKEN_SYMBOLS, TOKEN_ICON_SYMBOLS } from '../config';
import { Abi, parseEther, Address, isAddress, formatEther } from 'viem';
import { decodeEventLog } from 'viem';
import { toast } from 'react-toastify';
import { formatTokenAmount } from '../utils/formatting';
import { uploadFileToBackend, uploadDirectoryToBackend, checkBackendHealth } from '../utils/backendUpload';

interface OTCWizardProps {
  entityAddress: Address;
  onClose: () => void;
  onSuccess?: (productAddress: `0x${string}`) => void;
}

interface DeploymentDetails {
  hash: string;
  otcAddress?: `0x${string}`;
  status: 'pending' | 'complete' | 'error';
  error?: string;
}

interface UIGenerationDetails {
  status: 'pending' | 'complete' | 'error';
  error?: string;
  imageUrl?: string;
  uiPageUrl?: string;
}

export function OTCWizard({ entityAddress, onClose, onSuccess }: OTCWizardProps) {
  // Step management
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedAssets, setSelectedAssets] = useState<Set<Address>>(new Set());
  const [assetAmounts, setAssetAmounts] = useState<Record<string, string>>({});
  const [approvalStatus, setApprovalStatus] = useState<Record<string, boolean>>({});
  
  // OTC specific fields
  const [userBAddress, setUserBAddress] = useState('');
  const [userAAssets, setUserAAssets] = useState<Address[]>([]);
  const [userAAmounts, setUserAAmounts] = useState<string[]>([]);
  const [userBAssets, setUserBAssets] = useState<Address[]>([]);
  const [userBAmounts, setUserBAmounts] = useState<string[]>([]);
  const [expiryTime, setExpiryTime] = useState('');
  
  // UI Generation
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isGeneratingUI, setIsGeneratingUI] = useState(false);
  const [uiGenerationDetails, setUiGenerationDetails] = useState<UIGenerationDetails | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Transaction states
  const [isDeploying, setIsDeploying] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isFunding, setIsFunding] = useState(false);
  const [deploymentDetails, setDeploymentDetails] = useState<DeploymentDetails | null>(null);
  
  // Wagmi hooks
  const { address } = useAccount();
  const { chain } = useNetwork();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();

  // Available assets
  const availableAssets = [
    { address: ADDRESSES.FED as Address, name: 'FED' },
    { address: ADDRESSES.TRINITY_TOKEN as Address, name: 'Trinity Token' },
    { address: ADDRESSES.TBILL as Address, name: 'TBILL' },
    { address: ADDRESSES.PDAI as Address, name: 'pDAI' },
  ];

  // Get balances for all assets
  const assetBalances = availableAssets.map(asset => {
    const { data: balance } = useBalance({
      address,
      token: asset.address,
      watch: true,
    });
    return { ...asset, balance };
  });

  // Step 1: Deploy OTC Contract
  const deployProductArgs = useMemo(() => [
    'TrinityOTC',
    ADDRESSES.COUPON_TOKEN as Address,
    '0x0000000000000000000000000000000000000000' as Address,
    name,
    description
  ], [address, name, description]);

  const { write: deployProduct } = useContractWrite({
    address: entityAddress,
    abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
    functionName: 'deployProduct',
    args: deployProductArgs,
    onSuccess: async (tx) => {
      setDeploymentDetails({
        hash: tx.hash,
        status: 'pending'
      });
      
      try {
        const receipt = await publicClient.waitForTransactionReceipt({ hash: tx.hash });
        const productDeployedEvent = receipt.logs.find(log => {
          try {
            if (log.address.toLowerCase() !== entityAddress.toLowerCase()) return false;
            const event = decodeEventLog({
              abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
              data: log.data,
              topics: log.topics
            });
            return event.eventName === 'ProductDeployed';
          } catch {
            return false;
          }
        });

        if (!productDeployedEvent) throw new Error('No ProductDeployed event found');

        const event = decodeEventLog({
          abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
          data: productDeployedEvent.data,
          topics: productDeployedEvent.topics
        }) as unknown as { eventName: 'ProductDeployed'; args: { product: Address } };

        setDeploymentDetails({
          hash: tx.hash,
          otcAddress: event.args.product,
          status: 'complete'
        });
        
        // Move to next step
        setStep(3);
        setIsDeploying(false);
        toast.success('OTC contract created successfully!');
      } catch (error) {
        setDeploymentDetails(prev => ({
          ...prev!,
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error'
        }));
        setIsDeploying(false);
        toast.error('Failed to create OTC contract');
      }
    }
  });

  const handleSubmit = async () => {
    if (!name.trim() || !description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!isAddress(userBAddress)) {
      toast.error('Please enter a valid address for User B');
      return;
    }

    if (userAAssets.length === 0 && userBAssets.length === 0) {
      toast.error('Please add at least one asset for either user');
      return;
    }

    if (userAAssets.length !== userAAmounts.length || userBAssets.length !== userBAmounts.length) {
      toast.error('Asset and amount arrays must have the same length');
      return;
    }

    setIsDeploying(true);
    deployProduct?.();
  };

  const handleGenerateUI = async () => {
    if (!deploymentDetails?.otcAddress) {
      toast.error('No OTC contract address available');
      return;
    }

    setIsGeneratingUI(true);
    setUiGenerationDetails({ status: 'pending' });

    try {
      // Check backend health
      const isHealthy = await checkBackendHealth();
      if (!isHealthy) {
        throw new Error('Backend is not available');
      }

      // Generate OTC UI template
      const otcData = {
        otcName: name,
        otcDescription: description,
        otcAddress: deploymentDetails.otcAddress,
        imageUrl: imagePreview || '',
        supportedAssets: Array.from(selectedAssets),
        userBAddress,
        userAAssets,
        userAAmounts,
        userBAssets,
        userBAmounts,
        expiryTime
      };

      const templateFiles = generateOTCUITemplate(otcData);
      
      // Upload template to backend
      const uploadResult = await uploadDirectoryToBackend(templateFiles, 'otc-template');
      
      if (uploadResult.success && uploadResult.cid) {
        setUiGenerationDetails({
          status: 'complete',
          uiPageUrl: uploadResult.cid
        });
        
        // Set UI page on the contract
        if (deploymentDetails.otcAddress) {
          const otcContract = {
            address: deploymentDetails.otcAddress,
            abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi
          };
          
          // Get properties contract
          const propertiesAddress = await publicClient.readContract({
            ...otcContract,
            functionName: 'properties'
          });
          
          // Set UI page
          const propertiesContract = {
            address: propertiesAddress as Address,
            abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi
          };
          
          await publicClient.writeContract({
            ...propertiesContract,
            functionName: 'setUIpage',
            args: [1, uploadResult.cid]
          });
          
          toast.success('OTC UI template generated and uploaded successfully!');
          onSuccess?.(deploymentDetails.otcAddress);
        }
      } else {
        throw new Error('Failed to upload template');
      }
    } catch (error) {
      setUiGenerationDetails({
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      toast.error('Failed to generate UI template');
    } finally {
      setIsGeneratingUI(false);
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeploy = () => {
    handleSubmit();
  };

  const addUserAsset = (isUserA: boolean) => {
    const asset = availableAssets[0].address;
    const amount = '0';
    
    if (isUserA) {
      setUserAAssets([...userAAssets, asset]);
      setUserAAmounts([...userAAmounts, amount]);
    } else {
      setUserBAssets([...userBAssets, asset]);
      setUserBAmounts([...userBAmounts, amount]);
    }
  };

  const removeUserAsset = (isUserA: boolean, index: number) => {
    if (isUserA) {
      setUserAAssets(userAAssets.filter((_, i) => i !== index));
      setUserAAmounts(userAAmounts.filter((_, i) => i !== index));
    } else {
      setUserBAssets(userBAssets.filter((_, i) => i !== index));
      setUserBAmounts(userBAmounts.filter((_, i) => i !== index));
    }
  };

  const updateUserAsset = (isUserA: boolean, index: number, asset: Address) => {
    if (isUserA) {
      const newAssets = [...userAAssets];
      newAssets[index] = asset;
      setUserAAssets(newAssets);
    } else {
      const newAssets = [...userBAssets];
      newAssets[index] = asset;
      setUserBAssets(newAssets);
    }
  };

  const updateUserAmount = (isUserA: boolean, index: number, amount: string) => {
    if (isUserA) {
      const newAmounts = [...userAAmounts];
      newAmounts[index] = amount;
      setUserAAmounts(newAmounts);
    } else {
      const newAmounts = [...userBAmounts];
      newAmounts[index] = amount;
      setUserBAmounts(newAmounts);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gray-900 rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white">Create OTC Agreement</h2>
              <p className="text-gray-400">Step {step} of 3</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Step 1: Basic Information */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Basic Information</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      OTC Name
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter OTC name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter description"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      User B Address
                    </label>
                    <input
                      type="text"
                      value={userBAddress}
                      onChange={(e) => setUserBAddress(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0x..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Expiry Time (hours from now)
                    </label>
                    <input
                      type="number"
                      value={expiryTime}
                      onChange={(e) => setExpiryTime(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="24"
                      min="1"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={onClose}
                  className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setStep(2)}
                  disabled={!name.trim() || !description.trim() || !userBAddress.trim() || !expiryTime.trim()}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
                >
                  Next
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Asset Configuration */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Asset Configuration</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* User A Assets */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-white">Your Assets (User A)</h4>
                    
                    {userAAssets.map((asset, index) => (
                      <div key={index} className="flex gap-2">
                        <select
                          value={asset}
                          onChange={(e) => updateUserAsset(true, index, e.target.value as Address)}
                          className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                        >
                          {availableAssets.map((availableAsset) => (
                            <option key={availableAsset.address} value={availableAsset.address}>
                              {availableAsset.name}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          value={userAAmounts[index]}
                          onChange={(e) => updateUserAmount(true, index, e.target.value)}
                          className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                          placeholder="Amount"
                        />
                        <button
                          onClick={() => removeUserAsset(true, index)}
                          className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white"
                        >
                          <Minus size={16} />
                        </button>
                      </div>
                    ))}
                    
                    <button
                      onClick={() => addUserAsset(true)}
                      className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white font-medium"
                    >
                      <Plus size={16} className="inline mr-2" />
                      Add Asset
                    </button>
                  </div>

                  {/* User B Assets */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-white">User B Assets</h4>
                    
                    {userBAssets.map((asset, index) => (
                      <div key={index} className="flex gap-2">
                        <select
                          value={asset}
                          onChange={(e) => updateUserAsset(false, index, e.target.value as Address)}
                          className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                        >
                          {availableAssets.map((availableAsset) => (
                            <option key={availableAsset.address} value={availableAsset.address}>
                              {availableAsset.name}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          value={userBAmounts[index]}
                          onChange={(e) => updateUserAmount(false, index, e.target.value)}
                          className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                          placeholder="Amount"
                        />
                        <button
                          onClick={() => removeUserAsset(false, index)}
                          className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white"
                        >
                          <Minus size={16} />
                        </button>
                      </div>
                    ))}
                    
                    <button
                      onClick={() => addUserAsset(false)}
                      className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white font-medium"
                    >
                      <Plus size={16} className="inline mr-2" />
                      Add Asset
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
                >
                  <ArrowLeft size={16} className="inline mr-2" />
                  Back
                </button>
                <button
                  onClick={handleDeploy}
                  disabled={isDeploying || userAAssets.length === 0 && userBAssets.length === 0}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
                >
                  {isDeploying ? 'Deploying...' : 'Deploy OTC'}
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: UI Generation */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Generate UI Template</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Product Image (Optional)
                    </label>
                    <div
                      className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-gray-500 transition-colors"
                      onDrop={handleImageDrop}
                      onDragOver={handleDragOver}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {imagePreview ? (
                        <div className="space-y-2">
                          <img src={imagePreview} alt="Preview" className="w-32 h-32 object-cover mx-auto rounded-lg" />
                          <button
                            onClick={removeImage}
                            className="text-red-400 hover:text-red-300"
                          >
                            Remove Image
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Image size={48} className="mx-auto text-gray-400" />
                          <p className="text-gray-400">Click to upload or drag and drop</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </div>

                  {deploymentDetails?.status === 'complete' && (
                    <div className="bg-green-900/20 border border-green-500/20 rounded-lg p-4">
                      <p className="text-green-400">
                        ✅ OTC contract deployed successfully at: {deploymentDetails.otcAddress}
                      </p>
                    </div>
                  )}

                  {uiGenerationDetails?.status === 'complete' && (
                    <div className="bg-blue-900/20 border border-blue-500/20 rounded-lg p-4">
                      <p className="text-blue-400">
                        ✅ UI template generated and uploaded successfully!
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(2)}
                  className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
                >
                  <ArrowLeft size={16} className="inline mr-2" />
                  Back
                </button>
                <button
                  onClick={handleGenerateUI}
                  disabled={isGeneratingUI || !deploymentDetails?.otcAddress}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
                >
                  {isGeneratingUI ? 'Generating...' : 'Generate UI Template'}
                </button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function generateOTCUITemplate(data: {
  otcName: string;
  otcDescription: string;
  otcAddress: string;
  imageUrl: string;
  supportedAssets: Address[];
  userBAddress: string;
  userAAssets: Address[];
  userAAmounts: string[];
  userBAssets: Address[];
  userBAmounts: string[];
  expiryTime: string;
}): { [filename: string]: string } {
  const { otcName, otcDescription, otcAddress, imageUrl, supportedAssets, userBAddress, userAAssets, userAAmounts, userBAssets, userBAmounts, expiryTime } = data;

  return {
    'index.html': `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${otcName} - OTC Agreement</title>
    <link rel="stylesheet" href="style.css">
    <script src="lib/web3.min.js"></script>
</head>
<body>
    <div class="container">
        <header class="header">
            <div class="logo">
                <img src="lib/fed.png" alt="FED" class="logo-img">
                <img src="lib/pdai.png" alt="PDAI" class="logo-img">
            </div>
            <h1 class="title">${otcName}</h1>
            <p class="subtitle">${otcDescription}</p>
        </header>

        <main class="main">
            <div class="agreement-details">
                <h2>OTC Agreement Details</h2>
                <div class="details-grid">
                    <div class="detail-item">
                        <label>Contract Address:</label>
                        <span class="address">${otcAddress}</span>
                    </div>
                    <div class="detail-item">
                        <label>User B Address:</label>
                        <span class="address">${userBAddress}</span>
                    </div>
                    <div class="detail-item">
                        <label>Expiry Time:</label>
                        <span>${expiryTime} hours from creation</span>
                    </div>
                </div>
            </div>

            <div class="assets-section">
                <div class="user-assets">
                    <h3>Your Assets (User A)</h3>
                    <div class="assets-list">
                        ${userAAssets.map((asset, index) => `
                            <div class="asset-item">
                                <span class="asset-name">${asset}</span>
                                <span class="asset-amount">${userAAmounts[index]}</span>
                            </div>
                        `).join('')}
                    </div>
                </div>

                <div class="user-assets">
                    <h3>User B Assets</h3>
                    <div class="assets-list">
                        ${userBAssets.map((asset, index) => `
                            <div class="asset-item">
                                <span class="asset-name">${asset}</span>
                                <span class="asset-amount">${userBAmounts[index]}</span>
                            </div>
                        `).join('')}
                    </div>
                </div>
            </div>

            <div class="actions">
                <button id="depositBtn" class="btn btn-primary">Deposit Assets</button>
                <button id="claimBtn" class="btn btn-success" disabled>Claim Assets</button>
                <button id="withdrawBtn" class="btn btn-warning">Withdraw Assets</button>
                <button id="cancelBtn" class="btn btn-danger">Cancel Agreement</button>
            </div>

            <div id="status" class="status"></div>
        </main>
    </div>

    <script src="script.js"></script>
</body>
</html>`,

    'style.css': `
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

body {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    min-height: 100vh;
    color: #333;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}

.header {
    text-align: center;
    margin-bottom: 40px;
    background: rgba(255, 255, 255, 0.95);
    padding: 30px;
    border-radius: 15px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.logo {
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-bottom: 20px;
}

.logo-img {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.title {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 10px;
}

.subtitle {
    font-size: 1.1rem;
    color: #7f8c8d;
    font-weight: 300;
}

.main {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 15px;
    padding: 30px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.agreement-details {
    margin-bottom: 30px;
}

.agreement-details h2 {
    font-size: 1.8rem;
    color: #2c3e50;
    margin-bottom: 20px;
    text-align: center;
}

.details-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
    margin-bottom: 30px;
}

.detail-item {
    background: #f8f9fa;
    padding: 20px;
    border-radius: 10px;
    border-left: 4px solid #3498db;
}

.detail-item label {
    display: block;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 8px;
}

.address {
    font-family: 'Courier New', monospace;
    font-size: 0.9rem;
    color: #e74c3c;
    word-break: break-all;
}

.assets-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 30px;
    margin-bottom: 30px;
}

.user-assets {
    background: #f8f9fa;
    padding: 25px;
    border-radius: 10px;
    border-left: 4px solid #27ae60;
}

.user-assets h3 {
    font-size: 1.4rem;
    color: #2c3e50;
    margin-bottom: 20px;
    text-align: center;
}

.assets-list {
    space-y: 15px;
}

.asset-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    background: white;
    border-radius: 8px;
    margin-bottom: 10px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.asset-name {
    font-weight: 600;
    color: #2c3e50;
}

.asset-amount {
    font-weight: 600;
    color: #27ae60;
}

.actions {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
    margin-bottom: 20px;
}

.btn {
    padding: 15px 25px;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-primary {
    background: linear-gradient(135deg, #3498db, #2980b9);
    color: white;
}

.btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(52, 152, 219, 0.4);
}

.btn-success {
    background: linear-gradient(135deg, #27ae60, #229954);
    color: white;
}

.btn-success:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(39, 174, 96, 0.4);
}

.btn-warning {
    background: linear-gradient(135deg, #f39c12, #e67e22);
    color: white;
}

.btn-warning:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(243, 156, 18, 0.4);
}

.btn-danger {
    background: linear-gradient(135deg, #e74c3c, #c0392b);
    color: white;
}

.btn-danger:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(231, 76, 60, 0.4);
}

.status {
    padding: 20px;
    border-radius: 10px;
    text-align: center;
    font-weight: 600;
    margin-top: 20px;
}

.status.success {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
}

.status.error {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
}

.status.info {
    background: #d1ecf1;
    color: #0c5460;
    border: 1px solid #bee5eb;
}

@media (max-width: 768px) {
    .container {
        padding: 10px;
    }
    
    .title {
        font-size: 2rem;
    }
    
    .details-grid {
        grid-template-columns: 1fr;
    }
    
    .assets-section {
        grid-template-columns: 1fr;
    }
    
    .actions {
        grid-template-columns: 1fr;
    }
}`,

    'script.js': `
// OTC Agreement Management Script
class OTCAgreement {
    constructor() {
        this.web3 = null;
        this.contract = null;
        this.account = null;
        this.otcAddress = '${otcAddress}';
        this.userBAddress = '${userBAddress}';
        this.userAAssets = ${JSON.stringify(userAAssets)};
        this.userAAmounts = ${JSON.stringify(userAAmounts)};
        this.userBAssets = ${JSON.stringify(userBAssets)};
        this.userBAmounts = ${JSON.stringify(userBAmounts)};
        this.expiryTime = ${expiryTime};
        
        this.init();
    }

    async init() {
        try {
            // Check if MetaMask is installed
            if (typeof window.ethereum !== 'undefined') {
                this.web3 = new Web3(window.ethereum);
                
                // Request account access
                const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
                this.account = accounts[0];
                
                console.log('Connected to MetaMask:', this.account);
                this.updateStatus('Connected to MetaMask', 'info');
                
                // Initialize contract
                await this.initContract();
                
                // Set up event listeners
                this.setupEventListeners();
                
            } else {
                this.updateStatus('Please install MetaMask to use this application', 'error');
            }
        } catch (error) {
            console.error('Initialization error:', error);
            this.updateStatus('Failed to connect to MetaMask', 'error');
        }
    }

    async initContract() {
        try {
            // Basic contract ABI for OTC functions
            const contractABI = [
                {
                    "inputs": [{"internalType": "uint32", "name": "agreementId", "type": "uint32"}],
                    "name": "depositAssets",
                    "outputs": [],
                    "stateMutability": "nonpayable",
                    "type": "function"
                },
                {
                    "inputs": [{"internalType": "uint32", "name": "agreementId", "type": "uint32"}],
                    "name": "claimAssets",
                    "outputs": [],
                    "stateMutability": "nonpayable",
                    "type": "function"
                },
                {
                    "inputs": [{"internalType": "uint32", "name": "agreementId", "type": "uint32"}],
                    "name": "withdrawAssets",
                    "outputs": [],
                    "stateMutability": "nonpayable",
                    "type": "function"
                },
                {
                    "inputs": [{"internalType": "uint32", "name": "agreementId", "type": "uint32"}],
                    "name": "cancelAgreement",
                    "outputs": [],
                    "stateMutability": "nonpayable",
                    "type": "function"
                },
                {
                    "inputs": [{"internalType": "uint32", "name": "agreementId", "type": "uint32"}],
                    "name": "getAgreementDetails",
                    "outputs": [
                        {"internalType": "address", "name": "userA", "type": "address"},
                        {"internalType": "address", "name": "userB", "type": "address"},
                        {"internalType": "address[]", "name": "userAAssets", "type": "address[]"},
                        {"internalType": "uint256[]", "name": "userAAmounts", "type": "uint256[]"},
                        {"internalType": "address[]", "name": "userBAssets", "type": "address[]"},
                        {"internalType": "uint256[]", "name": "userBAmounts", "type": "uint256[]"},
                        {"internalType": "bool", "name": "userADeposited", "type": "bool"},
                        {"internalType": "bool", "name": "userBDeposited", "type": "bool"},
                        {"internalType": "bool", "name": "userAClaimed", "type": "bool"},
                        {"internalType": "bool", "name": "userBClaimed", "type": "bool"},
                        {"internalType": "bool", "name": "cancelled", "type": "bool"},
                        {"internalType": "uint64", "name": "createdAt", "type": "uint64"},
                        {"internalType": "uint64", "name": "expiryTime", "type": "uint64"},
                        {"internalType": "uint8", "name": "status", "type": "uint8"}
                    ],
                    "stateMutability": "view",
                    "type": "function"
                }
            ];

            this.contract = new this.web3.eth.Contract(contractABI, this.otcAddress);
            console.log('Contract initialized:', this.otcAddress);
            
        } catch (error) {
            console.error('Contract initialization error:', error);
            this.updateStatus('Failed to initialize contract', 'error');
        }
    }

    setupEventListeners() {
        const depositBtn = document.getElementById('depositBtn');
        const claimBtn = document.getElementById('claimBtn');
        const withdrawBtn = document.getElementById('withdrawBtn');
        const cancelBtn = document.getElementById('cancelBtn');

        if (depositBtn) {
            depositBtn.addEventListener('click', () => this.depositAssets());
        }
        if (claimBtn) {
            claimBtn.addEventListener('click', () => this.claimAssets());
        }
        if (withdrawBtn) {
            withdrawBtn.addEventListener('click', () => this.withdrawAssets());
        }
        if (cancelBtn) {
            cancelBtn.addEventListener('click', () => this.cancelAgreement());
        }
    }

    async depositAssets() {
        try {
            this.updateStatus('Depositing assets...', 'info');
            
            const result = await this.contract.methods.depositAssets(0).send({
                from: this.account,
                gas: 300000
            });
            
            console.log('Deposit transaction:', result);
            this.updateStatus('Assets deposited successfully!', 'success');
            
        } catch (error) {
            console.error('Deposit error:', error);
            this.updateStatus('Failed to deposit assets: ' + error.message, 'error');
        }
    }

    async claimAssets() {
        try {
            this.updateStatus('Claiming assets...', 'info');
            
            const result = await this.contract.methods.claimAssets(0).send({
                from: this.account,
                gas: 300000
            });
            
            console.log('Claim transaction:', result);
            this.updateStatus('Assets claimed successfully!', 'success');
            
        } catch (error) {
            console.error('Claim error:', error);
            this.updateStatus('Failed to claim assets: ' + error.message, 'error');
        }
    }

    async withdrawAssets() {
        try {
            this.updateStatus('Withdrawing assets...', 'info');
            
            const result = await this.contract.methods.withdrawAssets(0).send({
                from: this.account,
                gas: 300000
            });
            
            console.log('Withdraw transaction:', result);
            this.updateStatus('Assets withdrawn successfully!', 'success');
            
        } catch (error) {
            console.error('Withdraw error:', error);
            this.updateStatus('Failed to withdraw assets: ' + error.message, 'error');
        }
    }

    async cancelAgreement() {
        try {
            this.updateStatus('Cancelling agreement...', 'info');
            
            const result = await this.contract.methods.cancelAgreement(0).send({
                from: this.account,
                gas: 300000
            });
            
            console.log('Cancel transaction:', result);
            this.updateStatus('Agreement cancelled successfully!', 'success');
            
        } catch (error) {
            console.error('Cancel error:', error);
            this.updateStatus('Failed to cancel agreement: ' + error.message, 'error');
        }
    }

    updateStatus(message, type) {
        const statusElement = document.getElementById('status');
        if (statusElement) {
            statusElement.textContent = message;
            statusElement.className = 'status ' + type;
        }
        console.log('Status:', message);
    }
}

// Initialize the OTC Agreement when the page loads
document.addEventListener('DOMContentLoaded', () => {
    new OTCAgreement();
});`,

    'lib/web3.min.js': `// Web3.js library content would be here`,
    'lib/fed.png': `// FED token image`,
    'lib/pdai.png': `// PDAI token image`
  };
} 